import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from "@angular/core";
import * as _ from "lodash";
import {PlanningViewSettingsRoutesModel} from "../../../models/planning-view-settings.model";
import {PlanningRouteModel} from "../../../models/planning-route.model";
import {LocationActivityModel} from "../../../models/location-activity.model";
import {Address} from "../../../../addresses/models/address.model";

require('./planning-draft-routes-panel-list-item.scss');
@Component({
  standalone: false,
  selector: 'app-planning-draft-routes-panel-list-item',
  templateUrl: './planning-draft-routes-panel-list-item.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningDraftRoutesPanelListItemComponent implements OnInit, OnChanges {

  @Input() draft: any;
  @Input() route: PlanningRouteModel;
  @Input() logisticsVehicleName?: string;
  @Input() jobs: any[];
  @Input() settings: PlanningViewSettingsRoutesModel;
  @Input() isSelected: boolean;
  @Input() isVisible: boolean;
  @Input() canSelect?: boolean;
  @Input() canClose?: boolean;
  @Input() canDrilldown?: boolean;
  @Input() itemElementSize?: string;
  @Input() isShowActionMenu: boolean;
  @Output() onToggle = new EventEmitter<boolean>();
  @Output() onToggleVisible = new EventEmitter<boolean>();
  @Output() onSelect = new EventEmitter<any>();
  @Output() onLockRouteChange = new EventEmitter<{ id: string, locked: boolean }>();
  @Output() onChangeRouteResource = new EventEmitter<{ routeId: string, event: any }>();
  @Output() onEditResourceAllocation = new EventEmitter<{ route: any, event: any }>();
  @Output() onDispatch = new EventEmitter<string>();
  @Output() onReoptimize = new EventEmitter<string>();
  @Output() onPreAssignAgent = new EventEmitter<string>();
  @Output() onPrint = new EventEmitter<string>();
  @Output() onPrintLoadingSheet = new EventEmitter<string>();
  @Output() onDelete = new EventEmitter<string>();
  @Output() onFeedback = new EventEmitter<{ routeId: string, feedback: string }>();
  @Output() onClose = new EventEmitter<any>();
  @Output() onDrillDown = new EventEmitter<{ routeId: string, event: any }>();

  public capacity: {
    type: string;
    value: string;
    maxValue: string;
    icon: string;
    percentage: number;
  };
  public routeColor: string;
  public accountIds: string[] = [];
  public dateRange: { notBefore: string; notAfter: string };
  public isHover: boolean;
  public isShowCopyBtn: boolean = false;
  public isCollapsed: boolean = false;
  public isRunning: boolean = false;
  public pickingTime: any;
  public weightSeverity: string = '';
  public weightUnit: string = this.splitService.getShortWeightUnit();
  public volumeSeverity: string = '';
  public volumeCapacity: number = 0;
  public volumeUnit: string = this.splitService.getVolumeUnit();
  public palletsSeverity: string = '';
  public severityThresholds: { low: number; mid: number; } = { low: 80, mid: 99 };
  public accountsDetailsMap: Map<string, { name: string; externalId: string; pallets: number; address?: Address }> = new Map<string, { name: string; externalId: string; pallets: number; address: Address}>();
  public accountsDetails: Array<{ accountId: string; name: string; externalId: string; pallets: number; address?: Address }> = [];
  public accountExternalIds: string[] = [];

  constructor(@Inject('planningCommonService') private planningCommonService, @Inject('$timeout') private $timeout, @Inject('splitService') private splitService) {

  }

  ngOnInit() {
    this.setRouteColor();
    this.setRoutePickingTime();
    this.setDateRange();
    this.setAccountsDetails();
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes.settings && !changes.settings.isFirstChange()) || (changes.route && !changes.route.isFirstChange())) {
      this.setRouteColor();
      this.setRoutePickingTime();
      this.setDateRange();
      this.setAccountsDetails();
      this._setIsRouteProcessRunning();
    }

    this.weightSeverity = this._getSeverity(this.route.totals.totalLoad, this.route.resource.capacity.weight);
    this.volumeCapacity = this.route.resource.capacity.dimensions ?
      (this.route.resource.capacity.dimensions.height * this.route.resource.capacity.dimensions.width * this.route.resource.capacity.dimensions.length) : 0;
    this.volumeSeverity = this._getSeverity(this.route.totals.totalVolume, this.volumeCapacity);
    this.palletsSeverity = this._getSeverity(this.route.totals.totalPallets, this.route.resource.capacity.numberOfPallets);
  }

  onHover(isHover) {
    this.isHover = isHover;
    this.$timeout(0).then(() => {
    });
  }

  onClick($event) {
    this.onSelect.emit($event);
    this.$timeout(0).then(() => {
    });
  }

  onToggleLocked() {
    this.onLockRouteChange.emit({id: this.route.id, locked: !this.route.locked});
  }

  onEditResourceAllocationClick($event) {
    this.onEditResourceAllocation.emit({route: this.route, event: $event});
    $event.stopPropagation();
  }

  onChangeRouteResourceClick($event) {
    this.onChangeRouteResource.emit({routeId: this.route.id, event: $event});
    $event.stopPropagation();
  }

  onToggleCollapse() {
    this.isCollapsed = !this.isCollapsed;
    this.$timeout(0).then(() => {
    });
  }

  onDrillDownClick($event) {
    $event.stopPropagation();
    this.onDrillDown.emit({routeId: this.route.id, event: $event});
  }

  getTotalIncomeClass(totalIncome?) {
    const minimumRouteIncome = this.route.resource.routeIncomePreferences?.minimumRouteIncome || this.draft?.settings?.incomeSettings?.minRouteIncome;
    const maximumRouteIncome = this.route.resource.routeIncomePreferences?.maximumRouteIncome || this.draft?.settings?.incomeSettings?.maxRouteIncome;
    return (!!minimumRouteIncome && (totalIncome < minimumRouteIncome)) ? 'low' :
      ((!!maximumRouteIncome && (totalIncome > maximumRouteIncome)) ? 'high' : '');
  }

  private setRouteColor() {
    this.routeColor = this.planningCommonService.getRouteColor(this.route);
  }

  private setRoutePickingTime() {
    const routeStartTime = this.route.activities?.[0]?.eta || this.route.activities?.[0]?.notBefore;
    this.pickingTime = {
      time: routeStartTime ? new Date(routeStartTime).getTime() - ((this.route.pickingTimeOffsetInMinutes || 0) * 60000) : undefined
    }
  }

  private setDateRange() {
    this.dateRange = {
      notBefore: _.get(_.head(this.route.activities), 'eta'),
      notAfter: _.get(_.last(this.route.activities), 'etd')
    }
  }

  private _setIsRouteProcessRunning() {
    this.isRunning = this.route.actionProcess?.running && (new Date().getTime() < this.route.actionProcess?.expirationDate);
  }

  private _getSeverity(value: number, capacity?: number): string {
    if (!capacity) {
      return '';
    }
    const ratio = (value / capacity) * 100;
    return (ratio < this.severityThresholds.low) ? 'low' : ((ratio >= this.severityThresholds.low && ratio < this.severityThresholds.mid) ? 'mid' : 'high');
  }

  private setAccountsDetails() {
    let hasDropoffs: boolean = false;
    const flatActivities: LocationActivityModel[] = _.flatMap(this.route.activities, (activity: LocationActivityModel) => {
      if (activity.type === 'START' || activity.type === 'END') {
        return [];
      }

      if (activity.stopType === 'Dropoff') {
        hasDropoffs = true;
      }

      return activity.type === 'GROUP' ? (activity.stopActivities || []) : [activity];
    });
    const sideActivities = hasDropoffs
      ? _.filter(flatActivities, (a: LocationActivityModel) => a.stopType === 'Dropoff')
      : _.filter(flatActivities, (a: LocationActivityModel) => a.stopType !== 'Dropoff');

    this.accountsDetailsMap = new Map();
    sideActivities.forEach((a: LocationActivityModel) => {
      let accountValue: { name: string; externalId: string; pallets: number; address?: Address} | undefined = this.accountsDetailsMap.get(a.accountId);

      if (!accountValue) {
        accountValue = {name: a.accountName, externalId: a.accountExternalId, pallets: 0, address: a.address};
      }

      const packages = this.jobs?.find(job => job.id === a.jobId)?.packages || [];

      accountValue.pallets = packages.reduce((acc, pkg) => acc + (pkg.pallets || 0), accountValue.pallets);
      this.accountsDetailsMap.set(a.accountId, accountValue);
    });
    this.accountIds = Array.from(this.accountsDetailsMap.keys());
    this.accountExternalIds = _.compact(_.uniq(Array.from(this.accountsDetailsMap.values()).map(d => d.externalId)));

    // accountsDetailsMap preserves insertion order (first occurrence in sideActivities);
    // build an array from it since the `keyvalue` pipe would otherwise re-sort keys alphabetically.
    this.accountsDetails = Array.from(this.accountsDetailsMap.entries()).map(([accountId, details]) => ({ accountId, ...details }));
  }
}
